import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const MachineDowntime = () => {
  // State variables for Machine Downtime calculation
  const [totalDowntimeHours, setTotalDowntimeHours] = useState(0); // Total downtime hours
  const [totalOperationalHours, setTotalOperationalHours] = useState(0); // Total operational hours
  const [downtime, setDowntime] = useState(0); // Calculated downtime percentage

  // Event handlers to update state values
  const handleDowntimeChange = (e) => setTotalDowntimeHours(parseFloat(e.target.value) || 0);
  const handleOperationalHoursChange = (e) => setTotalOperationalHours(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the Machine Downtime
  useEffect(() => {
    if (totalOperationalHours > 0) {
      const downtimePercentage = (totalDowntimeHours / totalOperationalHours) * 100;
      setDowntime(downtimePercentage);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [totalDowntimeHours, totalOperationalHours]);

  return (
    <Layout title="Machine Downtime Calculator" path="machine-downtime">
      <section className="section-padding" id="machine-downtime">
        <div className="container mt-5">
          <div className="row">
            {/* Machine Downtime Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Machine Downtime Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Downtime Hours */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalDowntimeHours" className="form-label">
                        Total Downtime Hours
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Downtime Hours:</strong> The total hours the machine was not operational."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalDowntimeHours"
                        placeholder="Enter total downtime hours"
                        value={totalDowntimeHours || ""}
                        onChange={handleDowntimeChange}
                      />
                    </div>

                    {/* Input for Total Operational Hours */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalOperationalHours" className="form-label">
                        Total Operational Hours
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Operational Hours:</strong> The total planned operational hours of the machine."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalOperationalHours"
                        placeholder="Enter total operational hours"
                        value={totalOperationalHours || ""}
                        onChange={handleOperationalHoursChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Downtime:{" "}
                        <span className="fw-bold ms-3">
                          {downtime === 0
                            ? "0"
                            : downtime.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Machine Downtime
                </h3>

                {/* What is Machine Downtime? */}
                <h4 className="mt-3 mb-3">What is Machine Downtime?</h4>
                <p>
                  **Machine Downtime** refers to the period during which machinery is non-operational due to maintenance, breakdowns, or other issues. It is a critical metric for assessing productivity losses in manufacturing and production environments.
                </p>

                {/* Why Use the Machine Downtime Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Machine Downtime Calculator?</h4>
                <p>
                  The Machine Downtime Calculator helps identify inefficiencies in production processes and optimize maintenance schedules. By reducing downtime, businesses can enhance productivity and minimize losses.
                </p>

                {/* Benefits of Using the Machine Downtime Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Machine Downtime Calculator</h4>
                <ul>
                  <li><strong>Helps Identify Productivity Losses:</strong> Provides insights into how much production time is lost due to downtime.</li>
                  <li><strong>Optimizes Maintenance Schedules:</strong> Assists in planning preventive maintenance to minimize disruptions.</li>
                  <li><strong>Supports Continuous Improvement:</strong> Enables businesses to track and improve operational efficiency.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Machine Downtime Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Manufacturing Teams:</strong> Helps monitor and reduce downtime in production facilities.</li>
                  <li><strong>Production Facilities:</strong> Assists in improving overall equipment effectiveness (OEE).</li>
                  <li><strong>Maintenance Teams:</strong> Provides data to plan and execute timely maintenance activities.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MachineDowntime;
